import React, { useEffect, useRef } from "react";
import { MyUsageFilterProps } from "./MyUsage.interfaces";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { DateTime } from 'luxon';
// @ts-ignore
import { ensureLuxonDateOnly } from '../../lib/Formatters';
// @ts-ignore
import { useMyTimezone } from 'components/customer/orgs/MyOrgAPIEndpoints';
// @ts-ignore
import { MyUsageDatePicker } from './MyUsageDatePicker';
import { Formik, Form, Field, FormikProps } from 'formik';
import useAccount from "../../lib/useAccount";
import { USAGE_FILTER_TYPES } from "./MyUsage.constants";
import { Alert, Grid } from "@mui/material";
import { UsageHelpers } from "./MyUsage.helpers";
import UcSelectBillingCycle from "../common/UcSelectBillingCycle";
// @ts-ignore
import { useBillPeriods } from 'components/readings/BillPeriodAPIEndpoint';
// @ts-ignore
import { dateRangeFormatterIgnoreUtc } from 'lib/Formatters';

interface MyUsageFilterFormValues {
    account: any;
    startDate: Date | number;
    endDate: Date | number;
    endingOn: Date;
    endingOnMonthly: Date;
    endingOnYearly: Date;
}

function MyUsageFilter(props: Readonly<MyUsageFilterProps>) {
    const dialogSubmit = useRef(props.dialogSubmit);
    const formRef = useRef<FormikProps<MyUsageFilterFormValues>>(null);
    const { t } = useTranslation();
    const timezone = useMyTimezone();
    const { account } = useAccount();
    const [showBillingPeriodsWarning, setShowBillingPeriodsWarning] = React.useState(false);
    const [billingPeriodOptions, setBillingPeriodOptions] = React.useState<any[]>([]);

    const minDate = DateTime.fromISO(account?.minStartTimestamp).toUTC().toISO();
    const maxDate = DateTime.fromISO(account?.maxEndTimestamp).toUTC().toISO();

    // MONTHLY SELECTOR START
    const monthlySelectorfilter = React.useMemo(() => ({
        take: 100,
        skip: 0,
        excludeFutureStart: true,
        excludeFutureEnd: null,
        sort: [{ field: "startTimestamp", sort: "desc" }],
        generalSearch: "",
        startTimeUTC: minDate,
        endTimeUTC: maxDate,
        accountId: account?.id,
        status: "ACTIVE",
        uom: props.uom,
        serialNo: props.serialNo
    }), [account?.id, maxDate, minDate, props.uom, props.serialNo]);

    const swrBillPeriods = useBillPeriods(monthlySelectorfilter);

    useEffect(() => {
        if (!swrBillPeriods.isLoading) {
            let billPeriodOptions: any[] = [];
            if (swrBillPeriods.result?.length > 0) {
                swrBillPeriods.result.forEach((item: any) => {
                    billPeriodOptions.push({
                        value: DateTime.fromJSDate(new Date(item.startDateTimeUtc)).toUTC().toISO(),
                        label: dateRangeFormatterIgnoreUtc(item.startDate, item.endDate, true)
                    });
                });

                setShowBillingPeriodsWarning(false);
            }
            else {
                setShowBillingPeriodsWarning(true);
            }

            setBillingPeriodOptions(billPeriodOptions);
        }
    }, [swrBillPeriods.isLoading, swrBillPeriods.result]);

    // MONTHLY SELECTOR END

    const getDateFilterInputFormat = () => {
        switch (props.chartType) {
            case USAGE_FILTER_TYPES.DAY:
                return "MMM/dd/yyyy";
            case USAGE_FILTER_TYPES.YEAR:
            case USAGE_FILTER_TYPES.MONTH:
            default:
                return "MMM/yyyy";
        }
    }

    const getDateFilterViews = () => {
        let views = ['day'];

        if (props.chartType === USAGE_FILTER_TYPES.YEAR || props.chartType === USAGE_FILTER_TYPES.MONTH) {
            views = ['month', 'year'];
        }

        return views;
    }

    const getChartMaxDaysLabel = () => {
        if (props.chartType === USAGE_FILTER_TYPES.YEAR) {
            return t("Month/Year (Past {{maxDays}} Months)", { maxDays: 12 });
        }
        if (props.chartType === USAGE_FILTER_TYPES.MONTH) {
            return t("Billing Period");
        }
        return t("Past {{maxHours}} Hours (Day Start to End)", { maxHours: 24 });
    }
    React.useEffect(() => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    }, [formRef?.current?.values.endingOnMonthly]);

    React.useEffect(() => {
        if (props.dialogSubmit !== dialogSubmit.current) {
            dialogSubmit.current = props.dialogSubmit;
            if (formRef.current) {
                formRef.current.handleSubmit();
            }
        }
    });

    // Used for chart arrows - Update endingOn fields when startDate/endDate changes
    useEffect(() => {
        if (!formRef.current || !props.startDate || !props.endDate) return;

        const startDate = new Date(props.startDate);
        const endDate = new Date(props.endDate);

        switch (props.chartType) {
            case USAGE_FILTER_TYPES.DAY:
                formRef.current.setFieldValue('endingOn', DateTime.fromJSDate(startDate).toUTC().toISO());
                break;
            case USAGE_FILTER_TYPES.MONTH:
                formRef.current.setFieldValue('endingOnMonthly', DateTime.fromJSDate(startDate).toUTC().toISO());
                break;
            case USAGE_FILTER_TYPES.YEAR: {
                const currentEndingDate = formRef.current?.values?.endingOnYearly
                    ? DateTime.fromJSDate(new Date(formRef.current.values.endingOnYearly))
                    : DateTime.now();

                const endDateTime = DateTime.fromJSDate(endDate);
                const startDateTime = DateTime.fromJSDate(startDate);
                const newEndingDate = endDateTime.set({ year: endDateTime.year, month: startDateTime.month, day: currentEndingDate.day }).toUTC().toISO();

                formRef.current.setFieldValue('endingOnYearly', newEndingDate);
                break;
            }
        }
    }, [props.startDate, props.endDate, props.chartType]);

    return (
        <div className="myUsageFilter ignoreCustomFormStyles">
            <Formik
                innerRef={formRef}
                initialValues={props.defaultValues}
                onSubmit={(values) => {
                    switch (props.chartType) {
                        case USAGE_FILTER_TYPES.YEAR: {
                            const endingOnDateTimeYearly = DateTime.fromISO(values.endingOnYearly);

                            let yearStart = UsageHelpers.createDateFromObject(endingOnDateTimeYearly.year, endingOnDateTimeYearly.month, endingOnDateTimeYearly.day, timezone, 'yearStart');
                            const yearEnd = UsageHelpers.createDateFromObject(endingOnDateTimeYearly.year, endingOnDateTimeYearly.month, endingOnDateTimeYearly.day, timezone, 'yearEnd', -24);

                            if (yearStart < minDate) {
                                yearStart = UsageHelpers.createDateFromObject(minDate.year, minDate.month, minDate.day, timezone, 'yearStart');
                            }

                            values.startDate = yearStart;
                            values.endDate = yearEnd;

                            break;
                        }
                        case USAGE_FILTER_TYPES.MONTH: {                          
                            const endingOnDateTimeMonthly = DateTime.fromISO(values.endingOnMonthly);
                           
                            const monthStart = UsageHelpers.createDateFromObject(endingOnDateTimeMonthly.year, endingOnDateTimeMonthly.month, endingOnDateTimeMonthly.day, timezone);
                            const monthEnd = UsageHelpers.createDateFromObject(endingOnDateTimeMonthly.year, endingOnDateTimeMonthly.month, endingOnDateTimeMonthly.day, timezone, 'monthEnd');

                            values.startDate = monthStart;
                            values.endDate = monthEnd;

                            break;
                        }  
                        case USAGE_FILTER_TYPES.DAY: {
                            const endingOnDateTime = DateTime.fromISO(values.endingOn);
                            const dayStart = UsageHelpers.createDateFromObject(endingOnDateTime.year, endingOnDateTime.month, endingOnDateTime.day, timezone, 'hourStart', 0);
                            const dayEnd = UsageHelpers.createDateFromObject(endingOnDateTime.year, endingOnDateTime.month, endingOnDateTime.day, timezone, 'hourEnd');

                            values.startDate = dayStart;
                            values.endDate = dayEnd;
                            break;
                        }
                        default:
                            break;
                    }

                    values.account = account;
   
                    if (isNaN(values.startDate as number) || isNaN(values.endDate as number)) {
                        values.startDate = new Date(props.defaultValues.startDate);
                        values.endDate = new Date(props.defaultValues.endDate);
                    }

                    props.onSubmit(values);
                }}
            >
                <Form style={{ paddingTop: '6px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12}>
                            {props.chartType === USAGE_FILTER_TYPES.DAY && (
                                <MyUsageDatePicker
                                    dataCyId={"myUsageFilter-day"}
                                    label={getChartMaxDaysLabel()}
                                    name={'endingOn'}
                                    minDate={ensureLuxonDateOnly(minDate)}
                                    maxDate={ensureLuxonDateOnly(maxDate)}
                                    inputFormat={getDateFilterInputFormat()}
                                    views={getDateFilterViews()}
                                />
                            )}
                            {props.chartType === USAGE_FILTER_TYPES.MONTH && (
                                <Field
                                    name={'endingOnMonthly'}
                                    label={getChartMaxDaysLabel()}
                                    component={UcSelectBillingCycle}
                                    options={billingPeriodOptions}
                                />
                            )}
                            {props.chartType === USAGE_FILTER_TYPES.YEAR && (
                                <MyUsageDatePicker
                                    dataCyId={"myUsageFilter-year"}
                                    label={getChartMaxDaysLabel()}
                                    name={'endingOnYearly'}
                                    minDate={ensureLuxonDateOnly(minDate)}
                                    maxDate={ensureLuxonDateOnly(maxDate)}
                                    inputFormat={getDateFilterInputFormat()}
                                    views={getDateFilterViews()}
                                />
                            )}
                        </Grid>
                    </Grid>
                    {showBillingPeriodsWarning && (
                        <Alert sx={{ mt: 2 }} severity="warning">{t("No billing cycle information available for this account.")}</Alert>
                    )}
                </Form>
            </Formik>
        </div>
    );
}

export default MyUsageFilter;